/**
 * DemoRoleSwitcher — In-Session Role Preview
 *
 * Shows all 7 district personas in a compact switcher panel.
 * Click any role → switches JWT via /api/auth/demo-token (Bearer re-auth)
 * → updates localStorage → redirects to role's home page.
 *
 * AND WHICH DISTRICT. Dev holds 11 districts; this panel could only ever reach
 * Central City, because demo-token minted Central City and nothing else. The
 * district picker below is the surface for the `district` parameter — it is how a
 * person signs in as Boston's superintendent, which is what makes tenant
 * isolation something you can be shown rather than something a test asserts.
 *
 * The list is READ, from GET /api/auth/demo-districts, never hardcoded: a
 * hardcoded list goes stale the first time a district is added, and standing up
 * a new district is the thing this is for.
 *
 * Only renders for demo sessions (isDemo=true in localStorage).
 *
 * Appears at bottom of sidebar, above sign-out button.
 *
 * @version 1.1.0
 */

import React, { useState, useCallback, useEffect } from 'react';
import { getHomeForRole } from '../../config/pageRoles.js';

// ============================================================================
// PERSONAS (mirrors backend demo-token personas + principal)
// ============================================================================

const DEMO_PERSONAS = [
    {
        id: 'superintendent',
        label: 'Superintendent',
        name: 'Dr. Maria Rodriguez',
        icon: '🏛️',
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        activeColor: 'bg-purple-600 text-white border-purple-600',
        description: 'Strategic KPIs, equity, ROI',
    },
    {
        id: 'principal',
        label: 'Principal',
        name: 'Jennifer Chen',
        icon: '🏫',
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        activeColor: 'bg-orange-600 text-white border-orange-600',
        description: 'School overview, staff, attendance',
    },
    {
        id: 'cio',
        label: 'CIO',
        name: 'Dr. David Thompson',
        icon: '🔒',
        color: 'bg-teal-50 text-teal-700 border-teal-200',
        activeColor: 'bg-teal-600 text-white border-teal-600',
        description: 'Security, compliance, agents',
    },
    {
        id: 'counselor',
        label: 'Counselor',
        name: 'Robert Williams',
        icon: '🤝',
        color: 'bg-rose-50 text-rose-700 border-rose-200',
        activeColor: 'bg-rose-600 text-white border-rose-600',
        description: 'Caseload, at-risk, deadlines',
    },
    {
        id: 'teacher',
        label: 'Teacher',
        name: 'Angela Garcia',
        icon: '📚',
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        activeColor: 'bg-blue-600 text-white border-blue-600',
        description: 'My students, grades, PD',
    },
    {
        id: 'district',
        label: 'District Admin',
        name: 'Full Access',
        icon: '⚙️',
        color: 'bg-slate-100 text-slate-700 border-slate-300',
        activeColor: 'bg-slate-700 text-white border-slate-700',
        description: 'All dashboards, all roles',
    },
];

// ============================================================================
// COMPONENT
// ============================================================================

export default function DemoRoleSwitcher() {
    const [switching, setSwitching] = useState(null); // persona id being switched to
    const [expanded, setExpanded] = useState(false);
    const [error, setError] = useState(null);
    const [districts, setDistricts] = useState([]);

    // Check if this is a demo session
    const isDemo = isDemoSession();
    const currentRole = getCurrentDemoRole();
    const currentDistrict = getCurrentDistrict();

    // The district list, read once. A FAILURE HERE IS NOT FATAL: demo-districts
    // is 404 in production and unreachable offline, and a picker that threw would
    // take the role switcher — the part that already worked — down with it. No
    // list means no picker, and roles still switch inside the current district.
    useEffect(() => {
        if (!isDemo) return;
        let live = true;
        (async () => {
            try {
                const res = await fetch('/api/auth/demo-districts');
                if (!res.ok) return;
                const data = await res.json();
                if (live && Array.isArray(data.districts)) setDistricts(data.districts);
            } catch {
                /* offline or gated — the picker simply does not appear */
            }
        })();
        return () => { live = false; };
    }, [isDemo]);

    /**
     * @param personaId  the role to become
     * @param districtSlug  the district to become it in; defaults to the one the
     *   session is already in. Passing it explicitly is what makes changing
     *   district a switch rather than a separate action — the server keeps the
     *   session's district when none is named, so "stay put" needs no argument.
     */
    const handleSwitch = useCallback(async (personaId, districtSlug) => {
        const targetDistrict = districtSlug || currentDistrict.slug || null;
        const sameRole = personaId === currentRole;
        const sameDistrict = targetDistrict === (currentDistrict.slug || null);
        if ((sameRole && sameDistrict) || switching) return;

        setSwitching(personaId);
        setError(null);

        try {
            // Get current token for Bearer re-auth (no password needed)
            const token = getSessionToken();
            if (!token) {
                // Fallback: redirect to login
                window.location.href = '/admin/login';
                return;
            }

            const response = await fetch('/api/auth/demo-token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    portalType: personaId,
                    // Omitted, not null, when there is nothing to say: the server
                    // treats absence as "stay in this session's district".
                    ...(targetDistrict ? { district: targetDistrict } : {}),
                }),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || `Switch failed (${response.status})`);
            }

            const data = await response.json();

            // Update localStorage session
            const existingAuth = JSON.parse(localStorage.getItem('agenticed_admin_auth') || '{}');
            localStorage.setItem('agenticed_admin_auth', JSON.stringify({
                ...existingAuth,
                token: data.token,
                type: personaId === 'staff' || personaId === 'vendor' ? personaId : 'district',
                demoRole: personaId,
                identifier: data.user?.name || existingAuth.identifier,
                userId: data.user?.userId || existingAuth.userId,
                email: data.user?.email || existingAuth.email,
                isDemo: true,
                loggedInAt: Date.now(),
                // Remembered so the next role switch stays in this district and
                // the panel can say which district it is showing.
                districtSlug: targetDistrict || null,
                districtName: data.tenant?.name || null,
            }));

            // Update session token for API calls
            localStorage.setItem('agenticed_session', data.token);

            // Update tenant branding if provided
            if (data.tenant?.branding) {
                localStorage.setItem('agenticed_tenant_branding', JSON.stringify(data.tenant.branding));
            }

            // Navigate to role-specific home page
            const homePath = getHomeForRole(personaId);
            window.location.href = `/admin${homePath}`;
        } catch (err) {
            console.error('[DemoRoleSwitcher] Switch failed:', err);
            setError(err.message || 'Role switch failed. Try logging out and back in.');
            setSwitching(null);
        }
    }, [currentRole, currentDistrict.slug, switching]);

    // Don't render for non-demo sessions or vendor portal
    const portalType = (() => { try { return JSON.parse(localStorage.getItem('agenticed_admin_auth') || '{}').type || 'district'; } catch { return 'district'; } })();
    if (!isDemo || portalType === 'vendor') return null;

    const currentPersona = DEMO_PERSONAS.find(p => p.id === currentRole);

    return (
        <div className="px-3 py-2">
            {/* Collapsed: show current role + expand button */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
            >
                <span className="text-base">{currentPersona?.icon || '👤'}</span>
                <div className="flex-1 text-left min-w-0">
                    <p className="text-xs font-semibold text-amber-300 truncate">
                        Demo: {currentPersona?.label || currentRole}
                    </p>
                    <p className="text-[10px] text-amber-400/70 truncate">
                        {currentDistrict.name || currentPersona?.name || ''}
                    </p>
                </div>
                <svg
                    className={`w-4 h-4 text-amber-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {/* Expanded: all personas */}
            {expanded && (
                <div className="mt-2 space-y-1 animate-in slide-in-from-bottom-2">
                    {error && (
                        <div className="px-2.5 py-2 bg-red-500/20 border border-red-500/30 rounded-lg mb-2">
                            <p className="text-[11px] text-red-300">{error}</p>
                        </div>
                    )}
                    {districts.length > 0 && (
                        <div className="mb-2">
                            <label
                                htmlFor="demo-district-picker"
                                className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-2 mb-1"
                            >
                                District
                            </label>
                            <select
                                id="demo-district-picker"
                                value={currentDistrict.slug || ''}
                                disabled={!!switching}
                                onChange={(e) => handleSwitch(currentRole, e.target.value)}
                                className="w-full text-xs bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-200 disabled:opacity-50"
                            >
                                {!currentDistrict.slug && <option value="">Central City School District</option>}
                                {districts.map((d) => (
                                    <option key={d.slug} value={d.slug}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-2 mb-1">
                        Switch Role
                    </p>
                    {DEMO_PERSONAS.map((persona) => {
                        const isActive = persona.id === currentRole;
                        const isSwitching = persona.id === switching;

                        return (
                            <button
                                key={persona.id}
                                onClick={() => handleSwitch(persona.id, currentDistrict.slug)}
                                disabled={isActive || !!switching}
                                className={`
                                    w-full flex items-center gap-2 px-2 py-1.5 rounded-lg
                                    transition-all duration-150 text-left
                                    ${isActive
                                        ? 'bg-slate-700 text-white'
                                        : switching
                                            ? 'opacity-50 cursor-wait'
                                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                    }
                                `}
                            >
                                <span className="text-sm flex-shrink-0">
                                    {isSwitching ? (
                                        <span className="inline-block w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        persona.icon
                                    )}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate">{persona.label}</p>
                                    <p className="text-[10px] opacity-60 truncate">{persona.name}</p>
                                </div>
                                {isActive && (
                                    <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">
                                        Active
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ============================================================================
// HELPERS
// ============================================================================

function isDemoSession() {
    try {
        const auth = JSON.parse(localStorage.getItem('agenticed_admin_auth') || '{}');
        return auth.isDemo === true;
    } catch {
        return false;
    }
}

/**
 * The district this demo session is in: `{ slug, name }`, both null for the
 * Central City default (which carries no slug because nothing ever named one).
 */
function getCurrentDistrict() {
    try {
        const auth = JSON.parse(localStorage.getItem('agenticed_admin_auth') || '{}');
        return { slug: auth.districtSlug || null, name: auth.districtName || null };
    } catch {
        return { slug: null, name: null };
    }
}

function getCurrentDemoRole() {
    try {
        const auth = JSON.parse(localStorage.getItem('agenticed_admin_auth') || '{}');
        return auth.demoRole || 'district';
    } catch {
        return 'district';
    }
}

function getSessionToken() {
    try {
        const direct = localStorage.getItem('agenticed_session');
        if (direct) return direct;
        const auth = JSON.parse(localStorage.getItem('agenticed_admin_auth') || '{}');
        return auth.token || null;
    } catch {
        return null;
    }
}
